import {
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Textarea,
  ToggleButtonGroup,
  Typography,
} from '@healthinal/ui';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getGetScreeningTracingIndividualQueryKey,
  ScreeningAssessmentResult,
  useSetScreeningAssessmentResult,
} from '../../../../api/created/screening.ts';
import { queryClient } from '../../../../api/queryClient.ts';
import { FormActionButtons } from '../../../../components/form/FormActionButtons.tsx';
import { getScreeningAssessmentResultName } from '../../../../utils/screening.ts';

interface FormValues {
  result: ScreeningAssessmentResult | null;
  notes: string;
}

export function SetScreeningAssessmentResultDialog({
  screeningId,
  open,
  onClose,
  initialResult,
  initialNotes,
}: {
  screeningId: string;
  open: boolean;
  onClose: () => void;
  initialResult?: ScreeningAssessmentResult | null;
  initialNotes?: string | null;
}) {
  const {
    register,
    control,
    reset,
    handleSubmit,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>();

  const setResultMutation = useSetScreeningAssessmentResult({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetScreeningTracingIndividualQueryKey(screeningId) });
        handleOnClose();
      },
      onError: () => toast.error('Beim Setzen des Resultats ist ein Fehler aufgetreten'),
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        result: initialResult ?? null,
        notes: initialNotes ?? '',
      });
    }
  }, [open]);

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 700 }}>
        <ModalClose />
        <Typography level={'h4'}>Abschluss</Typography>
        <Divider />
        <form
          onSubmit={handleSubmit((data) =>
            setResultMutation.mutate({
              id: screeningId,
              data: {
                result: data.result!,
                notes: data.notes || null,
              },
            }),
          )}>
          <Stack gap={1}>
            <FormControl error={!!errors.result}>
              <FormLabel>
                <Typography>Resultat *</Typography>
              </FormLabel>
              <Controller
                name={`result`}
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <ToggleButtonGroup value={value} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {Object.values(ScreeningAssessmentResult).map((result) => (
                      <Button value={result} key={result} onClick={() => onChange(result)}>
                        {getScreeningAssessmentResultName(result)}
                      </Button>
                    ))}
                  </ToggleButtonGroup>
                )}
              />
              {errors.result && <FormHelperText>Resultat muss angegeben werden</FormHelperText>}
            </FormControl>
            <FormControl>
              <FormLabel>
                <Typography>Bemerkungen</Typography>
              </FormLabel>
              <Textarea {...register('notes')} minRows={2} maxRows={4} />
            </FormControl>
            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={setResultMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
